import React, { createContext, useContext, useState, ReactNode } from 'react';

type Role = 'super_admin' | 'normal_user';
const ACTIVE_COMPANY_KEY = 'waypoint_active_company_id';
const ACTIVE_ROLE_KEY = 'waypoint_active_role';

interface RoleContextType {
  role: Role;
  activeCompanyId: string;
  setRole: (role: Role) => void;
  setActiveCompanyId: (companyId: string) => void;
  toggleRole: () => void;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

export const RoleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [role, setRole] = useState<Role>(() => {
    if (typeof window === 'undefined') {
      return 'super_admin';
    }
    const storedRole = window.localStorage.getItem(ACTIVE_ROLE_KEY);
    return storedRole === 'normal_user' ? 'normal_user' : 'super_admin';
  });

  const [activeCompanyId, setActiveCompanyId] = useState<string>(() => {
    if (typeof window === 'undefined') {
      return '';
    }
    return window.localStorage.getItem(ACTIVE_COMPANY_KEY) ?? '';
  });

  const toggleRole = () => {
    setRole((prev) => (prev === 'super_admin' ? 'normal_user' : 'super_admin'));
  };

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(ACTIVE_ROLE_KEY, role);
    }
  }, [role]);

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(ACTIVE_COMPANY_KEY, activeCompanyId);
    }
  }, [activeCompanyId]);

  return (
    <RoleContext.Provider value={{ role, activeCompanyId, setRole, setActiveCompanyId, toggleRole }}>
      {children}
    </RoleContext.Provider>
  );
};

export const useRole = () => {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};
